// Badges (DESIGN §5.8 e §2.1): contagem sobre ícone, ponto de não lida, rótulo quadrado e faixa de ausência.
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { contagemBadge } from "../lib/numeros";

/** Contagem de não lidas sobre um ícone 24 (use dentro de um elemento `relative`). Some com 0. */
export function BadgeContagem({ valor, className }: { valor: number; className?: string }) {
  if (valor <= 0) return null;
  return (
    <span
      aria-hidden
      className={cn(
        "absolute -top-1 -right-1 grid h-5 min-w-5 place-items-center rounded-full bg-acao px-1.5",
        "text-[11px] font-bold leading-none text-sobre-acao placar ring-2 ring-painel",
        className,
      )}
    >
      {contagemBadge(valor)}
    </span>
  );
}

/** Ponto de "não lida" sem número. */
export function PontoNaoLido({ className, rotulo = "Não lida" }: { className?: string; rotulo?: string }) {
  return (
    <span
      role="img"
      aria-label={rotulo}
      className={cn("inline-block size-2.5 rounded-full bg-marca ring-2 ring-painel", className)}
    />
  );
}

export type TomRotulo = "neutro" | "tinta" | "ok" | "alerta" | "erro" | "info";

const TOM: Record<TomRotulo, string> = {
  neutro: "bg-recuo text-texto-2",
  tinta: "bg-tinta text-sobre-tinta",
  ok: "bg-ok-fundo text-ok",
  alerta: "bg-alerta-fundo text-alerta",
  erro: "bg-erro-fundo text-erro",
  info: "bg-info-fundo text-info",
};

/** Rótulo quadrado h-6 ("Rascunho", "Encerrado", "Vencida"). */
export function Rotulo({
  tom = "neutro",
  children,
  className,
}: {
  tom?: TomRotulo;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-6 items-center rounded-celula px-2 text-rotulo whitespace-nowrap",
        TOM[tom],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Faixa de ausência do painel (DESIGN §2.1): sempre número + texto. Segue as faixas da ACADEMIA
 * (`riscoAtencaoDias`/`riscoCriticoDias`), nunca cortes fixos. `faixa` (quando a API manda, ex.: LinhaRisco)
 * manda na cor; sem ela, a faixa sai de `dias` × `limiares`. Sem `limiares` (configuração ainda carregando),
 * o texto é "Atenção"/"Risco" em vez de "N+ dias".
 * Atenção: `bg-alerta-fundo text-alerta` com filete `risco-atencao` (6,77). Risco: branco sobre `risco-critico` (4,83).
 */
export function BadgeAusencia({
  dias,
  faixa,
  limiares,
  className,
}: {
  dias: number | null;
  faixa?: "ATENCAO" | "CRITICO" | null;
  limiares?: { atencao: number; critico: number } | null;
  className?: string;
}) {
  const f =
    faixa !== undefined
      ? faixa
      : dias === null || !limiares
        ? null
        : dias >= limiares.critico
          ? "CRITICO"
          : dias >= limiares.atencao
            ? "ATENCAO"
            : null;
  if (!f) return null;
  const corte = limiares ? (f === "CRITICO" ? limiares.critico : limiares.atencao) : null;
  const rotulo =
    corte === null ? (f === "CRITICO" ? "Risco" : "Atenção") : `${corte}+ ${corte === 1 ? "dia" : "dias"}`;
  return (
    <span
      className={cn(
        "inline-flex h-6 items-center rounded-celula px-2 text-rotulo whitespace-nowrap",
        f === "CRITICO"
          ? "bg-risco-critico text-white"
          : "bg-alerta-fundo text-alerta ring-1 ring-risco-atencao ring-inset",
        className,
      )}
      aria-label={
        dias === null
          ? f === "CRITICO"
            ? "Risco"
            : "Atenção"
          : `${dias} ${dias === 1 ? "dia" : "dias"} sem entrar, ${f === "CRITICO" ? "risco" : "atenção"}`
      }
    >
      {rotulo}
    </span>
  );
}

/**
 * Selo de vínculo (docs/prototipo/personais-agregadores.html §2 e §5, DESIGN §5.8): tipo de vínculo da pessoa com a
 * academia ("Personal", "Wellhub", "TotalPass"). Contorno, não preenchido: é identidade, não status; sem ícone e sem
 * cor de marca. 20 de altura para caber na linha de apoio (célula Aluno da tabela, meta do card no celular, segunda
 * linha das Entradas do dia, linha de selos da ficha), nunca na linha do nome. Só no painel da equipe.
 * Contraste: texto-2 sobre painel 8,0:1; contorno fio-controle 3,96:1.
 */
export function SeloVinculo({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 shrink-0 items-center rounded-[6px] bg-painel px-1.5 text-rotulo leading-none whitespace-nowrap text-texto-2 ring-1 ring-fio-controle ring-inset",
        className,
      )}
    >
      {children}
    </span>
  );
}
